import { createContext, useContext } from 'react';

export type VirtualPageViewOptions = {
  /**
   * The path to report. Usually a synthetic one that has no real URL, e.g.
   * `/mijn-eneco/gegevens/success` after a form save.
   */
  pagePath: string;
};

export type NavigationPageViewOptions = {
  /** The path the caller is about to (or has just) navigated to. */
  pagePath: string;
};

export type PageViewContextType = {
  trackVirtualPageView: (options: VirtualPageViewOptions) => void;
  /**
   * For a caller that also performs the navigation itself (e.g. a flow calling
   * `router.push`). Fires eagerly, before the browser has committed the new
   * URL, so it lands ahead of any synchronous event (a funnel step, say) the
   * same caller pushes right after starting the navigation — see
   * `usePageViewTracking` for why that ordering can't be achieved by waiting
   * for `routeChangeComplete`.
   */
  trackPageView: (options: NavigationPageViewOptions) => void;
};

/**
 * Defaults to no-ops so `useTracking` keeps working outside the provider — the
 * BE native app and most unit tests never mount `PageViewTracker`.
 */
export const PageViewContext = createContext<PageViewContextType>({
  trackVirtualPageView: () => {},
  trackPageView: () => {},
});

export const usePageView = () => useContext(PageViewContext);
